/**
 * fourHourBodySR — 4H body-close support/resistance cluster detection.
 *
 * The user's NinjaTrader templates expose a "4H S/R (Body)" level that
 * shows the most-frequented body-close price on the 4H chart over recent
 * history — e.g., the 2026-05-26 chart screenshots labeled "4H S/R
 * (Body) 29,731.25". Coach currently captures NONE of this.
 *
 * Algorithm (single-link clustering on 4H closes — same shape as
 * avwapConfluenceZones, intentionally; familiar code path):
 *   1. Collect finite close prices from the recent 4H bars (or all bars
 *      passed in — caller controls scope by slicing the array first).
 *   2. Sort ascending.
 *   3. Walk and group consecutive prices whose gap to the cluster's
 *      MAX is ≤ tolerance. Comparing to running-max keeps slow-drift
 *      clusters connected without splitting on each new addition.
 *   4. Keep clusters with size ≥ minClusterSize (default 3) — three or
 *      more 4H bars closing in a tight band is the threshold below which
 *      the cluster is just noise.
 *   5. Sort clusters by size descending, take top N (default 3), return
 *      their MEAN price.
 *
 * Output shape per Top-Down §5.1: `number[]` — array of cluster center
 * prices, ordered by cluster frequency (highest first).
 *
 * NOT used for actual entries/wicks/highs — only bar CLOSES. Wicks
 * represent rejection (price visited but didn't hold); body closes
 * represent acceptance (price held into the close). The user's "Body"
 * label tracks the latter, which is the stronger S/R signal.
 *
 * Pure function. No side effects.
 */

export const DEFAULT_OPTS = Object.freeze({
  /** Convergence tolerance in PRICE POINTS. Default 1.0 = 4 ticks on
   *  0.25-tick instruments (MNQ/NQ/ES/MES). YM gets a coarser band; OK
   *  for v1 — per-instrument tuning is a v2 refinement. */
  tolerancePoints: 1.0,
  /** Minimum 4H bars that must close in the same cluster for it to count
   *  as a recurrent S/R level. */
  minClusterSize: 3,
  /** Top-N clusters to emit (by descending frequency). */
  topN: 3,
});

/**
 * Detect recurrent 4H body-close clusters in a bars array.
 *
 * @param {Array<{close:number}>} bars4h  4H bars; only `close` is read.
 *                                        Caller is responsible for slicing
 *                                        to the desired window (e.g., last
 *                                        30 days = ~180 4H bars during RTH).
 * @param {Object} [opts]
 * @returns {number[]}  Cluster center prices, top N by frequency. Empty
 *                      array when no qualifying clusters exist.
 */
export const detectFourHourBodySR = (bars4h, opts = {}) => {
  if (!Array.isArray(bars4h) || bars4h.length === 0) return [];

  const tolerance = Number(opts.tolerancePoints ?? DEFAULT_OPTS.tolerancePoints);
  const minSize = Math.max(2, Math.floor(Number(opts.minClusterSize ?? DEFAULT_OPTS.minClusterSize)));
  const topN = Math.max(1, Math.floor(Number(opts.topN ?? DEFAULT_OPTS.topN)));
  if (!Number.isFinite(tolerance) || tolerance <= 0) return [];

  // 1. Collect finite closes.
  const closes = [];
  for (const bar of bars4h) {
    const c = Number(bar?.close);
    if (Number.isFinite(c)) closes.push(c);
  }
  if (closes.length < minSize) return [];

  // 2. Sort ascending.
  closes.sort((a, b) => a - b);

  // 3. Single-link clustering — same algorithm as avwapConfluenceZones.
  const clusters = [];
  let current = [closes[0]];
  let currentMax = closes[0];
  for (let i = 1; i < closes.length; i++) {
    const c = closes[i];
    if (c - currentMax <= tolerance) {
      current.push(c);
      if (c > currentMax) currentMax = c;
    } else {
      clusters.push(current);
      current = [c];
      currentMax = c;
    }
  }
  clusters.push(current);

  // 4. Filter by minClusterSize.
  const qualified = clusters.filter(cl => cl.length >= minSize);
  if (qualified.length === 0) return [];

  // 5. Sort by size descending, take top N, emit cluster MEAN.
  qualified.sort((a, b) => b.length - a.length);
  return qualified.slice(0, topN).map(cl =>
    cl.reduce((s, v) => s + v, 0) / cl.length
  );
};
